import React, { Component, FC } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import { NAMESPACE_INTERESTING_MATCHES } from 'app/constants/Context';
import { withinBettingContext } from 'app/contexts/betting';
import { IconSize, LiveIndicator } from 'app/design-system/components/LiveIndicator';
import { MatchStatus, MatchType, SlotType } from 'app/lib/betting/betting-entities';
import { TEAMS } from 'app/lib/betting/constants';

import { getDataGaEventByNamespace } from '../../utils/data-ga-event-by-namespace';
import MatchCenterLink from '../MatchCenterLink';
import { getMatchInformationComponent } from './components/MatchInformations';
import MatchObjectCell from './components/MatchObjectCell';
import { MatchStartTime } from './components/MatchStartTime';
import { MatchStreamingCtrl } from './components/MatchStreamingCtrl';
import { getMatchScoreboardCellsComponent } from './components/ScoreboardCells';

import './MatchDetails.scss';

// ---

type MatchDetailsProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  // TODO: write types, if you have some free time please :)
  // matchId: PropTypes.number.isRequired,
  // namespace: PropTypes.string,
};

export class MatchDetails extends Component<MatchDetailsProps> {
  render() {
    const {
      matchId,
      objects,
      matchType,
      matchName,
      started,
      startDate,
      isLive,
      $betting: { namespace },
    } = this.props;
    const matchCenterDataGaEvent = getDataGaEventByNamespace(namespace, 'detailView');
    return (
      <>
        {matchType === MatchType.HEAD_TO_HEAD && (
          <MatchCenterLink className="match-details-container"
                           dataGaEvent={matchCenterDataGaEvent}
                           matchId={matchId}>
            <ul className="match-title"
                data-test="match-title">
              {TEAMS.map((teamId) => (
                <MatchObjectCell key={teamId}
                                 entity={objects.get(teamId)} />
              ))}
            </ul>

            {started && (
              <ul className="match-scores">
                {this.renderScoreboardCells()}
              </ul>
            )}
          </MatchCenterLink>
        )}

        {matchType === MatchType.OUTRIGHT && (
          <MatchCenterLink matchId={matchId}
                           dataGaEvent={matchCenterDataGaEvent}
                           className={classNames('single-event', 'match-name')}>
            {matchName}
          </MatchCenterLink>
        )}

        <div className="match-information">
          {(namespace === NAMESPACE_INTERESTING_MATCHES && isLive) && (
            <LiveIndicator className="live-indicator-icon"
                           size={IconSize.SMALL} />
          )}

          {isLive
            ? this.renderMatchInformation()
            : <MatchStartTime value={startDate} />
          }

          <MatchStreamingCtrl matchId={matchId}
                              liveStreamGaEvent="live-broadcast"
                              favoritesGaEvent={getDataGaEventByNamespace(namespace, 'toFavorites')} />
        </div>
      </>
    );
  }

  renderScoreboardCells() {
    const MatchScoreboardCells = getMatchScoreboardCellsComponent(this.props.sportId);

    return (
      <MatchScoreboardCells matchId={this.props.matchId} />
    );
  }

  renderMatchInformation() {
    const MatchInformation = getMatchInformationComponent(
      this.props.sportId,
      this.props.isMatchNotStarted,
    );

    return (
      <MatchInformation matchId={this.props.matchId} />
    );
  }
}

const createMapStateToProps = (_: ReduxState, {
  $betting: {
    content: {
      selectors: {
        matches,
        createMatchObjectsSelector,
        isMatchStarted,
      },
    },
  },
  matchId,
}: BettingContextProps) => {
  const matchObjects = createMatchObjectsSelector(matchId);

  return (state: ReduxState) => {
    const match = matches(state).get(matchId);
    const isLive = match?.slotType === SlotType.LIVE;
    const isMatchNotStarted = isLive && match.status === MatchStatus.NOT_STARTED;

    return {
      objects: matchObjects(state),
      matchType: match.type,
      matchName: match.name,
      started: (match.slotType === SlotType.LIVE) && isMatchStarted(state, { matchId }),
      sportId: match.sportId,
      startDate: match.startDate,
      isLive,
      isMatchNotStarted,
    };
  };
};

export default compose<FC<MatchDetailsProps>>(
  withinBettingContext,
  connect(createMapStateToProps),
)(MatchDetails);
